import { Permission } from '@core/config/permissions.config';
import { WorkspaceType } from '@core/config/workspace-types.config';

/**
 * What a notification is *about*, so the inbox can be filtered by subject rather
 * than by severity, and so each item can offer a link to the page it refers to.
 *
 * The Notification service stores a title, a body and a severity. Chat items
 * also carry `conversationId`, which is enough to open the thread. Everything
 * else is classified from the English title emitted by the RabbitMQ handlers
 * in `src/backend/Notification/RabbitMq/Consumers`.
 */
export type NotifCategory = 'assessments' | 'people' | 'marketplace' | 'account';

export interface NotifActionSpec {
  /** i18n key under `notifications.actions.*`. */
  labelKey: string;
  /** Route segments, passed through WorkspaceRouteService.path(). */
  segments: string[];
  /** Hidden when the active role lacks this capability. */
  permission?: Permission;
  primary?: boolean;
  /** Absolute route that must not be workspace-rewritten (e.g. workspace picker). */
  absolute?: boolean;
  /** Extra query string, e.g. `{ conversation: '12' }` to open a chat thread. */
  queryParams?: Record<string, string>;
}

const MATCHERS: { test: RegExp; category: NotifCategory }[] = [
  { test: /\bquiz\b/i, category: 'assessments' },
  { test: /\b(group|organization|invitation from)\b/i, category: 'people' },
  { test: /\bpack\b/i, category: 'marketplace' },
  { test: /\b(payment|account deletion|welcome)\b/i, category: 'account' },
];

/**
 * Sign-in greetings are not inbox items. Older API rows and live pushes may
 * still arrive with this copy until every client is on a build that dropped them.
 */
export function isLoginWelcomeNotification(title: string, message = ''): boolean {
  return /^\s*welcome back\b/i.test(title) || /successfully logged in/i.test(message);
}

/** Best-effort subject for a notification; unmatched titles read as account news. */
export function classifyNotification(
  title: string,
  message = '',
  conversationId?: number | null,
): NotifCategory {
  if (conversationId) return 'people';
  const haystack = `${title} ${message}`;
  for (const { test, category } of MATCHERS) {
    if (test.test(haystack)) return category;
  }
  return 'account';
}

/** Emoji for the subject — severity still drives the colour via `iconClass`. */
export function categoryIcon(category: NotifCategory): string {
  switch (category) {
    case 'assessments':
      return '📝';
    case 'people':
      return '👥';
    case 'marketplace':
      return '🛍️';
    case 'account':
    default:
      return '⚙️';
  }
}

/**
 * Where a notification points. Learners are sent to their own surfaces (due
 * work, progress) and staff to the management ones; every action still declares
 * the capability it needs so the page can drop links a role cannot open.
 */
export function notificationActions(
  title: string,
  workspaceType: WorkspaceType | null,
  conversationId?: number | null,
): NotifActionSpec[] {
  if (conversationId) {
    return [
      {
        labelKey: 'openMessages',
        segments: ['messages'],
        permission: 'messages.view',
        primary: true,
        queryParams: { conversation: String(conversationId) },
      },
    ];
  }

  const t = title.toLowerCase();
  // Education calls a Group a Class and puts it on a different URL.
  const groupsRoute = workspaceType === 'education' ? ['classes'] : ['groups'];

  if (t.includes('quiz results ready')) {
    return [
      { labelKey: 'viewResult', segments: ['progress'], permission: 'progress.view', primary: true },
      { labelKey: 'openEvaluation', segments: ['evaluation'], permission: 'assessment.grade' },
    ];
  }
  if (t.includes('quiz now available') || t.includes('quiz reminder')) {
    return [
      { labelKey: 'startNow', segments: ['due'], permission: 'practice.take', primary: true },
      { labelKey: 'openCalendar', segments: ['calendar'], permission: 'campaign.view' },
    ];
  }
  if (t.includes('quiz ended') || t.includes('quiz removed') || t.includes('quiz unprepared')) {
    return [
      { labelKey: 'openEvaluation', segments: ['evaluation'], permission: 'assessment.grade', primary: true },
      { labelKey: 'openAssessments', segments: ['assessments'], permission: 'assessment.view' },
    ];
  }
  if (t.includes('invitation from')) {
    return [
      { labelKey: 'reviewInvitation', segments: ['/select-workspace'], absolute: true, primary: true },
    ];
  }
  if (t.includes('group')) {
    return [{ labelKey: 'openGroup', segments: groupsRoute, permission: 'classes.view', primary: true }];
  }
  if (t.includes('organization')) {
    return [
      { labelKey: 'openMembers', segments: ['settings', 'members'], permission: 'members.manage', primary: true },
      { labelKey: 'switchWorkspace', segments: ['/select-workspace'], absolute: true },
    ];
  }
  if (t.includes('pack synced')) {
    return [
      { labelKey: 'openLibrary', segments: ['library'], permission: 'library.view', primary: true },
      { labelKey: 'openMarketplace', segments: ['marketplace'], permission: 'marketplace.view' },
    ];
  }
  if (t.includes('pack')) {
    return [
      { labelKey: 'openMarketplace', segments: ['marketplace'], permission: 'marketplace.view', primary: true },
    ];
  }
  if (t.includes('payment')) {
    return [{ labelKey: 'openBilling', segments: ['settings', 'billing'], permission: 'billing.manage', primary: true }];
  }
  // Account-deletion notices and other unmatched account items.
  return [{ labelKey: 'openSettings', segments: ['settings'] }];
}
